export class ConnectorApi {
  static ENVIRONMENT_STAGES: { [key: string]: string } = {
    dev: "dev",
    beta: "beta",
    production: "v1",
  };
  /**
   * Get the Doctoralia Connector stage for the current environment
   */
  static getDoctoraliaConnectorStage() {
    let stage = "v1";
    if (
      ["dev", "homolog", "localhost"].some(function (p) {
        return window.location.hostname.includes(p);
      })
    ) {
      stage = "dev";
    }
    return stage;
  }

  /**
   * Get the Doctoralia Connector URL for the current environment or a preferred stage
   * @param preferredStage
   */
  static getDoctoraliaConnectorUrl(preferredStage?: string | null): {
    url: string;
    stage: string;
  } {
    const stage = preferredStage || ConnectorApi.getDoctoraliaConnectorStage();
    return { url: "https://calendar-connector.feegow.com/" + stage, stage };
  }

  /**
   * Fetch data from the Doctoralia Connector API
   * @param method HTTP method
   * @param route API route
   * @param preferredStage Preferred stage to fetch data from
   */
  static async fetch(
    method: string,
    route: string,
    preferredStage?: string | null
  ): Promise<any> {
    const { url: connectorUrl } =
      ConnectorApi.getDoctoraliaConnectorUrl(preferredStage);

    const token = sessionStorage.getItem("tk") ?? localStorage.getItem("tk");
    const requestHeaders = {
      ...(token ? { "x-access-token": token } : {}),
    };
    const response = await fetch(`${connectorUrl}${route}`, {
      method,
      headers: requestHeaders,
    });

    if (!response.ok) {
      throw new Error(`Fetch failed with statusCode ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Fetch the facility integration data from the Doctoralia Connector API
   * If the facility not integrated in current stage, it will try to fetch from the next stage
   * @param licenseId
   * @param preferredStage
   */
  static async fetchFacilityIntegration(
    licenseId: number,
    preferredStage?: string | null
  ): Promise<any> {
    const currentStage =
      preferredStage || ConnectorApi.getDoctoraliaConnectorStage();
    console.log(
      `[Feegow SPA] [Connector API] Fetch facility integration stage ${currentStage}`
    );
    try {
      const response = await ConnectorApi.fetch(
        "GET",
        `/internal/facilities/${licenseId}`,
        currentStage
      );
      if (
        currentStage !== "v1" &&
        (!response ||
          !response.id ||
          !response.internalDoctors ||
          !response.internalDoctors.length)
      ) {
        return this.fetchFacilityIntegration(licenseId, "v1");
      }
      return response;
    } catch (error) {
      if (currentStage !== "v1") {
        return this.fetchFacilityIntegration(licenseId, "v1");
      }
      throw error;
    }
  }

  /**
   * Find a doctor in the facility integration data
   * @param facilityIntegrationData
   * @param professionalId
   */
  static findDoctorInFacilityIntegration(
    facilityIntegrationData: any,
    professionalId: number | string
  ) {
    return facilityIntegrationData &&
      Array.isArray(facilityIntegrationData.internalDoctors)
      ? facilityIntegrationData.internalDoctors.find(
          (d: any) =>
            Number(d.feegowId) === Number(professionalId) ||
            Number(d.doctorsLicenses?.professionalId) === Number(professionalId)
        ) || null
      : null;
  }

  /**
   * Find an address in the doctor integration data
   * @param doctorIntegrationData
   * @param licenseId
   * @param feegowUnitId
   */
  static findAddressInDoctorIntegration(
    doctorIntegrationData: any | undefined,
    licenseId: number | string,
    feegowUnitId = 0
  ) {
    if (
      !doctorIntegrationData ||
      !Array.isArray(doctorIntegrationData.addresses)
    ) {
      return null;
    }
    return (
      doctorIntegrationData?.addresses.find(
        (a: any) =>
          parseInt(a.licenseId, 10) === Number(licenseId) &&
          Number(a.feegowUnitId) === Number(feegowUnitId)
      ) || null
    );
  }

  /**
   * Fetch the auto-login token for a facility or doctor
   * @param isAdmin
   * @param facilityId
   * @param doctorId
   * @param preferredStage
   */
  static async fetchAutoLoginToken(
    isAdmin: boolean,
    facilityId: number,
    doctorId: number,
    preferredStage?: string | null
  ): Promise<string> {
    const urlToFetch = isAdmin
      ? `/doctoralia/authentication/facilities/${facilityId}`
      : `/doctoralia/authentication/facilities/${facilityId}/doctors/${doctorId}`;
    const result = await ConnectorApi.fetch("GET", urlToFetch, preferredStage);

    console.log(
      `[Feegow SPA][Connector API] Generated a token with properties:`,
      {
        isAdmin,
        facilityId,
        doctorId,
        preferredStage,
      }
    );

    return result.auth_token;
  }
}
